/* eslint-disable security/detect-object-injection */
import { IBlock, PageTemplate, QueryParams } from '@bees-web/core/types'
import { GetServerSidePropsContext } from 'next'
import httpClient from '../../clients/httpClient'
import { blockUrlBuilder } from '../../hooks/useBlockSchema'
import { BlocksServerData } from '../../types/interfaces'
import getDeferredUrl from './getDeferredUrl'

interface DeferredUrlEntry {
  url: string
  traceId: string
}

interface FetchResult {
  url: string
  data: IBlock | null
}

const SSR_BLOCK_TIMEOUT_MS = 2000
const SSR_GLOBAL_TIMEOUT_MS = 3000
const SSR_MAX_DEPTH = 3

async function fetchBlock(
  url: string,
  context: GetServerSidePropsContext,
  requestTraceId: string,
  signal: AbortSignal,
  timeoutMs: number
): Promise<IBlock | null> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  const onGlobalAbort = () => controller.abort()
  signal.addEventListener('abort', onGlobalAbort, { once: true })

  try {
    return await httpClient<IBlock>({
      context,
      requestTraceId,
      signal: controller.signal,
    })(url)
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn(`[SSR] Block fetch aborted: ${url}`)
    } else {
      console.error(`[SSR] Error fetching block ${url}:`, error.message)
    }
    return null
  } finally {
    clearTimeout(timeoutId)
    signal.removeEventListener('abort', onGlobalAbort)
  }
}

function collectDeferredUrls(
  blocks: IBlock[],
  query: QueryParams,
  baseTraceId: string
): DeferredUrlEntry[] {
  const urls: DeferredUrlEntry[] = []

  for (const block of blocks) {
    const deferredUrl = getDeferredUrl(block, true)

    if (deferredUrl) {
      urls.push({
        url: blockUrlBuilder(deferredUrl.url, query, {
          state: false,
          globals: false,
        }),
        traceId: `${baseTraceId}:${block.name}-ssr`,
      })
    }

    if (block.blocks?.length) {
      urls.push(...collectDeferredUrls(block.blocks, query, baseTraceId))
    }
  }

  return urls
}

function collectNestedDeferredUrls(
  fetchedBlocks: FetchResult[],
  query: QueryParams,
  baseTraceId: string
): DeferredUrlEntry[] {
  const urls: DeferredUrlEntry[] = []

  for (const { data } of fetchedBlocks) {
    if (data?.blocks?.length) {
      urls.push(...collectDeferredUrls(data.blocks, query, baseTraceId))
    }
  }

  return urls
}

/**
 * Fetches deferred block data on the server with aggressive parallelism.
 *
 * Optimizations over naïve approach:
 * - Global timeout: the entire operation is capped at SSR_GLOBAL_TIMEOUT_MS.
 *   When it fires, every in-flight request is aborted and the cache so far
 *   is returned — the client fills gaps via SWR.
 * - Per-block timeout: individual slow blocks are aborted independently.
 * - URL deduplication: the same block URL is fetched only once.
 * - Reduced max depth: limits the breadth-first waves to SSR_MAX_DEPTH.
 * - All blocks within a depth level are fetched via a single Promise.all.
 */
export default async function getBlocksServerData(
  page: PageTemplate,
  context: GetServerSidePropsContext,
  query: QueryParams,
  baseRequestTraceId: string,
  options: {
    blockTimeoutMs?: number
    globalTimeoutMs?: number
    maxDepth?: number
  } = {}
): Promise<BlocksServerData> {
  const {
    blockTimeoutMs = SSR_BLOCK_TIMEOUT_MS,
    globalTimeoutMs = SSR_GLOBAL_TIMEOUT_MS,
    maxDepth = SSR_MAX_DEPTH,
  } = options

  const cache: BlocksServerData = {}
  const { blocks = [] } = page

  if (blocks.length === 0) return cache

  const globalController = new AbortController()
  const globalTimeout = setTimeout(() => globalController.abort(), globalTimeoutMs)

  const fetchedUrls = new Set<string>()

  try {
    let urlsToFetch = collectDeferredUrls(blocks, query, baseRequestTraceId)
    let currentDepth = 0

    while (urlsToFetch.length > 0 && currentDepth < maxDepth) {
      if (globalController.signal.aborted) break

      const uniqueEntries = urlsToFetch.filter(({ url }) => {
        if (fetchedUrls.has(url)) return false
        fetchedUrls.add(url)
        return true
      })

      if (uniqueEntries.length === 0) break

      const results: FetchResult[] = await Promise.all(
        uniqueEntries.map(async ({ url, traceId }) => {
          const data = await fetchBlock(
            url,
            context,
            traceId,
            globalController.signal,
            blockTimeoutMs
          )
          return { url, data }
        })
      )

      for (const { url, data } of results) {
        if (data) cache[url] = data
      }

      urlsToFetch = collectNestedDeferredUrls(results, query, baseRequestTraceId)
      currentDepth++
    }

    if (currentDepth >= maxDepth && urlsToFetch.length > 0) {
      console.warn(
        `[SSR] Max depth (${maxDepth}) reached, ${urlsToFetch.length} blocks skipped`
      )
    }
  } finally {
    clearTimeout(globalTimeout)
    if (!globalController.signal.aborted) globalController.abort()
  }

  return cache
}
